import { createFileRoute, notFound } from "@tanstack/react-router";
import { getProfile } from "api-client";

import { PublicProfileNotFound } from "src/components/PublicProfileNotFound";
import { loaderHandler } from "src/lib/loaderHandler";
import { PublicProfilePage } from "src/pages/PublicProfilePage";
import { getProfileContactFormQuestionnaire } from "src/profile/profileContactForm";
import { getPublicProfileUrl } from "src/profile/publicProfileUrl";

export const Route = createFileRoute("/c/$slug")({
  component: RoutePage,
  notFoundComponent: PublicProfileNotFound,
  ssr: true,
  async loader(args) {
    return await loaderHandler(async () => {
      const response = await getProfile({
        path: { slug: args.params.slug },
        throwOnError: false,
      });

      if (response.response?.status === 404) {
        throw notFound();
      }

      if (response.error) {
        throw response.error;
      }

      return {
        data: { profile: response.data.data },
      };
    });
  },
  head(args) {
    if (!args.loaderData?.data) {
      return { meta: [] };
    }

    const profile = args.loaderData.data.profile;
    const displayName = profile.displayName || `@${profile.slug}`;
    const title = `${displayName} | sztama`;
    const description =
      profile.bio || `Meet ${displayName} and explore their profile on sztama.`;
    const canonicalUrl = getPublicProfileUrl(profile.slug);
    const imageUrl = profile.avatarUrl ?? "https://sztama.app/og-image.png";
    const sameAs = profile.links
      .filter((link) => link.placement === "HEADER")
      .flatMap((link) => {
        const url = link.resource.url;
        return url?.startsWith("http://") || url?.startsWith("https://")
          ? [url]
          : [];
      });
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      url: canonicalUrl,
      name: title,
      description,
      mainEntity: {
        "@type": "Person",
        name: displayName,
        alternateName: `@${profile.slug}`,
        identifier: profile.id,
        ...(profile.avatarUrl ? { image: profile.avatarUrl } : {}),
        ...(sameAs.length > 0 ? { sameAs } : {}),
      },
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:site_name", content: "sztama" },
        { property: "og:image", content: imageUrl },
        { property: "og:image:alt", content: `${displayName} profile` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageUrl },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData).replace(/</g, "\\u003c"),
        },
      ],
    };
  },
  staticData: {
    getLayoutSettings() {
      return {
        showAuthSidebar: false,
        useAuthLayout: false,
      };
    },
  },
});

function RoutePage() {
  const loaderData = Route.useLoaderData();
  const profile = loaderData.data.profile;

  return (
    <PublicProfilePage
      profile={{
        ...profile,
        contactForm: profile.contactForm
          ? {
              buttonText: profile.contactForm.buttonText,
              questionnaire: profile.contactForm.questionnaire
                ? getProfileContactFormQuestionnaire(
                    profile.contactForm.questionnaire,
                  )
                : null,
            }
          : null,
      }}
    />
  );
}
